import { useContext } from 'react';

import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Button, Card, Stack, styled, Typography } from '@mui/material';

import { SubmitButton } from '../../../components/buttons';
import { FormContainer } from '../../../components/forms';
import { TextField } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars/AlertSnackbar';
import { Spinner } from '../../../components/spinners/Spinner';
import { ErrorMessages } from '../../../constants';
import { useGetTopic, useUpdateTopic } from '../../../lib/topic/hooks';

type TopicFormType = {
  title: string;
};

const TopicEditSchema = Yup.object().shape({
  title: Yup.string().required(ErrorMessages.REQUIRED_TOPIC_TITLE),
});

function TopicsEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const topicId = id ?? '';
  const { data: topic, isLoading: isLoadingTopic } = useGetTopic(topicId);
  const { mutateAsync: updateTopic, isLoading: isSubmitting } = useUpdateTopic();

  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);

  const getTopicErrorMessage = (error: unknown) => {
    const parseMessage = (message: unknown) => {
      if (typeof message === 'string') return message;
      if (Array.isArray(message)) {
        const messages = message.filter((item): item is string => typeof item === 'string');
        if (messages.length > 0) return messages.join(', ');
      }
      return null;
    };

    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object') {
      const apiError = error as {
        message?: unknown;
        duplicateTopics?: unknown;
        error?: unknown;
        response?: { data?: { message?: unknown; error?: unknown } };
      };
      const duplicateTopics = Array.isArray(apiError.duplicateTopics)
        ? apiError.duplicateTopics.filter((topic): topic is string => typeof topic === 'string')
        : [];
      const parsedMessage = parseMessage(apiError.message) ?? parseMessage(apiError.response?.data?.message);
      if (parsedMessage && duplicateTopics.length) {
        return `${parsedMessage}: ${duplicateTopics.join(', ')}`;
      }
      if (parsedMessage) {
        return parsedMessage;
      }
      const parsedError = parseMessage(apiError.error) ?? parseMessage(apiError.response?.data?.error);
      if (parsedError) {
        return parsedError;
      }
    }
    return ErrorMessages.SERVER_ERROR;
  };

  const handleSubmit = async (values: TopicFormType) => {
    try {
      await updateTopic({
        topicId: topicId,
        params: {
          title: values.title,
        },
      });

      setAlertSnackbar({
        message: 'Topics updated',
        severity: 'success',
        vertical: 'top',
      });
      navigate('/cms/topics');
    } catch (error) {
      setAlertSnackbar({
        message: getTopicErrorMessage(error),
        severity: 'error',
        vertical: 'top',
      });
    }
  };

  let initialValues: any;

  if (topic) {
    initialValues = topic;
  }

  const setInitValue = (value?: string | boolean) => value || ' ';
  return (
    <Container>
      <TopBar>
        <Typography variant="h5" component="h1">
          Edit Topic
        </Typography>
      </TopBar>

      <StyledCard>
        {isLoadingTopic && <Spinner />}
        {!isLoadingTopic && initialValues && (
          <Formik initialValues={initialValues} validationSchema={TopicEditSchema} validateOnBlur={true} onSubmit={handleSubmit}>
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <FormContainer>
                <Stack spacing={2}>
                  <TextField
                    error={touched.title && !!errors.title}
                    type="text"
                    name="title"
                    label="Name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.title}
                    helperText={setInitValue(touched.title && errors.title)}
                    disabled={isSubmitting}
                  />
                </Stack>

                <ButtonContainer>
                  <Buttons>
                    <SubmitButton type="submit" disabled={isSubmitting} loading={isSubmitting} variant="contained">
                      Save
                    </SubmitButton>
                    <Button onClick={() => navigate(-1)} type="button">
                      Cancel
                    </Button>
                  </Buttons>
                </ButtonContainer>
              </FormContainer>
            )}
          </Formik>
        )}
      </StyledCard>
    </Container>
  );
}

export default TopicsEdit;

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  display: 'flex',
});

const ButtonContainer = styled('div')({
  marginTop: '20px',
  alignSelf: 'stretch',
  flex: 1,
  display: 'flex',
  justifyContent: 'space-between',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',

  '& > *': {
    margin: '5px',
  },
});
